import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input, type InputProps } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
  /** Обёртка вокруг input + кнопки (для позиционирования). */
  containerClassName?: string;
  /**
   * Иконка/адорнмент слева внутри поля (напр. `<Lock />` на экране входа).
   * Позиционируется через `absolute left-*` самим вызывающим — мы только
   * рендерим его перед input внутри `relative`-обёртки.
   */
  startAdornment?: React.ReactNode;
}

/**
 * PasswordInput — поле ввода пароля с кнопкой-переключателем видимости
 * (глазок). Обёртка над `ui/input`, НЕ клон: переиспользует базовый `<Input>`
 * со всеми его токенами/фокус-кольцом.
 *
 * - `type` локально переключается password↔text (дефолт — скрыто).
 * - Кнопка `<button type="button">` не сабмитит форму.
 * - `aria-label` меняется «Показать пароль» / «Скрыть пароль», `aria-pressed`
 *   отражает текущее состояние → screen-reader friendly.
 * - Тач-таргет ≥40px (`h-10 w-10`), focus-ring через semantic `ring`-токен.
 * - `pr-10` на input, чтобы текст не заезжал под иконку.
 * - `forwardRef` + spread всех props → `register(...)` от react-hook-form и
 *   `aria-invalid` работают без изменений.
 * - Earth Tech: иконка `text-muted-foreground` → `hover:text-foreground`.
 */
export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  PasswordInputProps
>(({ className, containerClassName, startAdornment, ...props }, ref) => {
  const [visible, setVisible] = React.useState(false);

  return (
    <div className={cn('relative', containerClassName)}>
      {startAdornment}
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={cn('pr-10', className)}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'}
        aria-pressed={visible}
        className={cn(
          'absolute right-0 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center ' +
            'justify-center rounded-md text-muted-foreground transition-colors ' +
            'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 ' +
            'focus-visible:ring-ring focus-visible:ring-offset-1',
        )}
      >
        {visible ? (
          <EyeOff className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Eye className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
});
PasswordInput.displayName = 'PasswordInput';
